import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { notificationAPI, curriculumAPI, rosterAPI } from '../../services/api';
import CoachLayout from './CoachLayout';

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

export default function AnnouncementsPage() {
  const { authoringDomains } = useAuth();

  // Composer state
  const [domain, setDomain] = useState('');
  const [title, setTitle] = useState('');
  const [bodyMd, setBodyMd] = useState('');
  const [rosterId, setRosterId] = useState('');
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);

  // Domain + roster lists
  const [domains, setDomains] = useState([]);
  const [rosters, setRosters] = useState([]);

  // Past announcements
  const [announcements, setAnnouncements] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);

  useEffect(() => {
    curriculumAPI.listDomains()
      .then(({ data }) => {
        const doms = data.domains || [];
        setDomains(doms);
        if (doms.length > 0 && !domain) setDomain(doms[0].domain_id);
      })
      .catch(() => {});

    rosterAPI.list()
      .then(({ data }) => setRosters(data.rosters || []))
      .catch(() => setRosters([]));

    fetchAnnouncements();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAnnouncements = () => {
    setLoadingHistory(true);
    notificationAPI.listAnnouncements({ limit: 50 })
      .then(({ data }) => setAnnouncements(data.announcements || []))
      .catch(() => setAnnouncements([]))
      .finally(() => setLoadingHistory(false));
  };

  const handleSend = async () => {
    if (!domain || !title.trim()) return;
    setSending(true);
    setSendResult(null);
    try {
      const { data } = await notificationAPI.createAnnouncement({
        domain,
        title: title.trim(),
        body_md: bodyMd.trim(),
        roster_id: rosterId || null,
      });
      setSendResult({ success: true, sent: data.sent, total: data.total_recipients });
      setTitle('');
      setBodyMd('');
      setRosterId('');
      fetchAnnouncements();
    } catch (err) {
      setSendResult({ success: false, error: err.response?.data?.detail || err.message });
    } finally {
      setSending(false);
    }
  };

  const filteredRosters = rosters.filter(
    (r) => !domain || r.domain === domain || !r.domain
  );

  return (
    <CoachLayout title="Announcements">
      {/* ── Composer ──────────────────────────────────────────────────── */}
      <div className="coach-section">
        <div className="coach-section-header">
          <h2 className="coach-section-title">New Announcement</h2>
        </div>
        <div style={{ maxWidth: 600, display: 'flex', flexDirection: 'column', gap: 12 }}>
          {/* Domain selector */}
          <div>
            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: 4, color: '#374151' }}>
              Domain
            </label>
            <select
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px',
                borderRadius: 8,
                border: '1px solid var(--border-color, #d1d5db)',
                fontSize: '0.9rem',
                background: 'var(--surface-primary, #fff)',
              }}
            >
              {domains.map((d) => (
                <option key={d.domain_id} value={d.domain_id}>
                  {d.display_name || d.domain_id}
                </option>
              ))}
            </select>
          </div>

          {/* Roster (optional) */}
          <div>
            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: 4, color: '#374151' }}>
              Audience
            </label>
            <select
              value={rosterId}
              onChange={(e) => setRosterId(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px',
                borderRadius: 8,
                border: '1px solid var(--border-color, #d1d5db)',
                fontSize: '0.9rem',
                background: 'var(--surface-primary, #fff)',
              }}
            >
              <option value="">All enrolled learners</option>
              {filteredRosters.map((r) => (
                <option key={r.roster_id} value={r.roster_id}>
                  {r.name || r.roster_id} ({(r.learner_ids || []).length} members)
                </option>
              ))}
            </select>
          </div>

          {/* Title */}
          <div>
            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: 4, color: '#374151' }}>
              Title *
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. New practice schedule released"
              style={{
                width: '100%',
                padding: '8px 12px',
                borderRadius: 8,
                border: '1px solid var(--border-color, #d1d5db)',
                fontSize: '0.9rem',
              }}
            />
          </div>

          {/* Body (markdown) */}
          <div>
            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: 4, color: '#374151' }}>
              Message (Markdown supported)
            </label>
            <textarea
              value={bodyMd}
              onChange={(e) => setBodyMd(e.target.value)}
              placeholder="Write your announcement here..."
              rows={5}
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: 8,
                border: '1px solid var(--border-color, #d1d5db)',
                fontSize: '0.9rem',
                resize: 'vertical',
                fontFamily: 'inherit',
              }}
            />
          </div>

          {/* Send button */}
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <button
              className="btn btn-primary"
              disabled={sending || !title.trim() || !domain}
              onClick={handleSend}
              style={{ opacity: (sending || !title.trim() || !domain) ? 0.5 : 1 }}
            >
              {sending ? 'Sending...' : 'Send Announcement'}
            </button>
            {sendResult && (
              <span style={{
                fontSize: '0.85rem',
                color: sendResult.success ? '#059669' : '#dc2626',
                fontWeight: 500,
              }}>
                {sendResult.success
                  ? `Sent to ${sendResult.sent} learner${sendResult.sent !== 1 ? 's' : ''}`
                  : `Error: ${sendResult.error}`}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* ── History ───────────────────────────────────────────────────── */}
      <div className="coach-section">
        <div className="coach-section-header">
          <h2 className="coach-section-title">Sent Announcements</h2>
        </div>

        {loadingHistory ? (
          <div className="coach-loading">Loading…</div>
        ) : announcements.length === 0 ? (
          <div className="coach-empty">
            <span className="coach-empty-icon">{'\uD83D\uDCE2'}</span>
            <div className="coach-empty-title">No announcements yet</div>
            <div className="coach-empty-desc">Send your first announcement above.</div>
          </div>
        ) : (
          <table className="coach-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Domain</th>
                <th>Recipients</th>
                <th>Read</th>
                <th>Sent</th>
              </tr>
            </thead>
            <tbody>
              {announcements.map((a) => (
                <tr key={a.announcement_id}>
                  <td style={{ fontWeight: 600 }}>{a.title}</td>
                  <td className="text-muted text-sm">{a.domain}</td>
                  <td className="text-muted text-sm">{a.recipient_count || 0}</td>
                  <td>
                    <span style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                      fontSize: '0.85rem',
                    }}>
                      <span style={{ fontWeight: 600 }}>{a.read_count || 0}</span>
                      <span className="text-muted">/ {a.recipient_count || 0}</span>
                    </span>
                  </td>
                  <td className="text-muted text-sm">{timeAgo(a.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </CoachLayout>
  );
}
